import { HStack, forwardRef, useDisclosure, useToast } from '@hover/blueprint';
import type { StackProps } from '@hover/blueprint';
import React from 'react';

import { useImageTracking } from 'src/modules/homeownerApp/components/Customize/useImageTracking';
import type { ButtonProps } from 'src/shared/components';
import { Button } from 'src/shared/components';
import { useUpdateAiDisplayImageMutation } from 'src/shared/graphql/generated';
import { buttonPressed } from 'src/shared/telemetry';

import { disableLoaderSlide } from '../../helpers/styles';
import { useHomeownerAppContext, useHomeownerTranslate } from '../../hooks';

import { DiscardModal } from './DiscardModal';
import { useCloseCustomize } from './useCloseCustomize';
import { useCustomization } from './useCustomization';

export interface CustomizeActionsProps extends StackProps {}

const buttonProps = {
  colorScheme: 'light',
  size: { base: 'small', tablet: 'medium' },
  sx: disableLoaderSlide,
} satisfies ButtonProps;

export const CustomizeActions = forwardRef<CustomizeActionsProps, 'div'>(
  (props, ref) => {
    const t = useHomeownerTranslate();
    const doneButtonText = t('customize.done');

    const { targetImage, customizedImage, history, customization } =
      useCustomization();
    const { trackingData: contextTrackingData } = useHomeownerAppContext();
    const imageTracking = useImageTracking();

    const hasHistory =
      history.entries.filter(
        ({ customization: entryCustomization }) => !!entryCustomization,
      ).length > 0;
    const hasUnsavedChanges =
      customizedImage &&
      targetImage.aiGeneratedImage.id !== customizedImage?.id;

    const toast = useToast({
      containerStyle: { width: 'auto' },
      helpers: true,
    });

    const discardModal = useDisclosure();

    const {
      preload,
      isClosing,
      save: saveCustomization,
      cancel: cancelCustomization,
    } = useCloseCustomize({
      image: targetImage,
      customizedImage,
    });

    const handleCancelClick = async () => {
      if (hasHistory) {
        discardModal.onOpen();
      } else {
        await cancelCustomization();
      }
    };

    const [updateAiDisplayImage, { loading: isUpdatingDisplayImage }] =
      useUpdateAiDisplayImageMutation();

    const updateDisplayImage = async () => {
      if (!customizedImage) return false;

      const { errors } = await updateAiDisplayImage({
        variables: {
          id: targetImage.id,
          aiGeneratedImageId: customizedImage.id,
        },
      });

      return !errors;
    };

    const save = async () => {
      if (!customizedImage) return;

      buttonPressed({
        ...contextTrackingData,
        ...imageTracking,
        button_text: doneButtonText,
        primary_cta: false,
        product_name: customization?.product.primaryLabel,
        product_group: customization?.product.palettes[0],
        track_id: 'edit-complete',
      });

      const success = await updateDisplayImage();

      if (success) {
        await saveCustomization();
        return;
      }

      const toastResponse = toast({
        description: t('customize.saveError'),
        position: 'top',
        props: {
          isError: true,
          action: {
            label: t('customize.retry'),
            callback: async () => {
              const { update, close: closeToast } = toastResponse;
              update({ props: { isLoading: true } });

              await save();

              closeToast();
            },
          },
        },
      });
    };

    const handleDoneClick = async () => save();

    return (
      <HStack justifyContent="space-between" {...props} ref={ref}>
        <Button
          isLoading={isClosing}
          onClick={handleCancelClick}
          onMouseEnter={preload}
          variant={{ base: 'ghost', tablet: 'secondary' }}
          {...buttonProps}
        >
          {t('customize.cancel')}
        </Button>
        <Button
          isDisabled={!hasUnsavedChanges}
          isLoading={isClosing || isUpdatingDisplayImage}
          onClick={handleDoneClick}
          onMouseEnter={preload}
          variant={{ base: 'ghost', tablet: 'primary' }}
          {...buttonProps}
        >
          {doneButtonText}
        </Button>
        <DiscardModal
          onDiscard={async () => {
            discardModal.onClose();
            await cancelCustomization();
          }}
          {...discardModal}
        />
      </HStack>
    );
  },
);
